import { DefaultEventEntity, EventEntity, SaveEventPayload } from '@src/entities/Event/includes/types';
import { fieldValidator } from '@src/shared/includes/validation';
import { Button } from '@src/shared/ui/components/Button';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Loader } from '@src/shared/ui/components/Loader';
import { useAssets } from '@src/shared/ui/hooks/useAssets';
import { useForm, useFormReturn } from '@uhg-abyss/web/hooks/useForm';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { DateInput } from '@uhg-abyss/web/ui/DateInput';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { SelectInputMulti } from '@uhg-abyss/web/ui/SelectInputMulti';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { TextInputArea } from '@uhg-abyss/web/ui/TextInputArea';
import { isEmpty, isEqual, merge } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import fields from './includes/fields.json';

const { actualRecordCount, endDate, eventTypeCode, expectedRecordCount, impactedSystems, notes, startDate, title } =
  fields as Record<string, any>;

type EventType = {
  codeDesc: string;
  codeId: number | string;
};

type ImpactedSystem = {
  codeDesc: string;
  codeId: number | string;
};

type Assets = {
  ListEventTypes?: { data: EventType[] };
  ListImpactedSystems?: { data: ImpactedSystem[] };
};

type FormModalProps = {
  currentEntity?: EventEntity;
  handleClose: () => void;
  handleSave: (payload: SaveEventPayload) => Promise<void>;
};

type SelectOption = Readonly<{ label: string; value: number | string }>;

/**
 * Form: Event
 *
 * Renders a form for creating or editing an event entity.
 * Handles form state, validation, and submission.
 */
export function Form({ currentEntity, handleClose, handleSave }: FormModalProps) {
  const { assets, isLoadingAssets }: { assets: Assets; isLoadingAssets: boolean } = useAssets([
    {
      apiKey: 'Event',
      queryKey: 'ListEventTypes',
    },
    {
      apiKey: 'Source',
      queryKey: 'ListImpactedSystems',
    },
  ]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [initialValues, setInitialValues] = useState<EventEntity>(DefaultEventEntity);

  const form: useFormReturn<EventEntity> = useForm({ defaultValues: DefaultEventEntity });
  const { defaultValues, errors, isDirty, isSubmitting, isValid } = form.formState;
  const formValues = form.getValues();

  /**
   * Loads current entity data into the form if editing.
   */
  useEffect(() => {
    if (!isEmpty(currentEntity)) {
      const data: EventEntity = merge({}, DefaultEventEntity, {
        actualRecordCount: String(currentEntity?.actualRecordCount ?? ''),
        endDate: dayjs(currentEntity?.endDate).format('MM/DD/YYYY') ?? '',
        eventTypeCode: currentEntity?.eventTypeCode ?? '',
        expectedRecordCount: String(currentEntity?.expectedRecordCount ?? ''),
        impactedSystems: currentEntity?.impactedSystems ?? [],
        notes: currentEntity?.notes ?? '',
        startDate: dayjs(currentEntity?.startDate).format('MM/DD/YYYY') ?? '',
        title: currentEntity?.title ?? '',
      });

      setInitialValues(data);

      form.reset(data, {
        keepDirty: false,
        keepDirtyValues: false,
        keepErrors: false,
        keepIsValid: false,
        keepSubmitCount: true,
        keepTouched: false,
        keepValues: false,
      });
    }
  }, [currentEntity]);

  /**
   * Handles form submission.
   * @param {SaveEventPayload} submittedValues - The values to submit.
   */
  const handleSubmit = useCallback(
    async (submittedValues: SaveEventPayload): Promise<void> => {
      if (!isLoading && isValid && !isSubmitting && isDirty) {
        setIsLoading(true);
        handleClose();
        await handleSave(submittedValues);
        setIsLoading(false);
      }
    },
    [isLoading, isDirty, isValid, isSubmitting, errors, currentEntity, handleClose, handleSave]
  );

  /**
   * Enables or disables the submit button based on form state.
   */
  useEffect(() => {
    if (!isEmpty(currentEntity)) {
      if (isEqual(formValues, initialValues) || !isValid) {
        setIsDisabled(true);
      } else {
        setIsDisabled(false);
      }
    } else if (!isValid) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [currentEntity, isDirty, isValid, formValues, defaultValues, initialValues]);

  /**
   * Option values for impacted systems dropdown select input.
   */
  const impactedSystemOptions: SelectOption[] = useMemo(() => {
    let theOptions: SelectOption[] = [];

    if (assets?.ListImpactedSystems?.data) {
      theOptions = assets.ListImpactedSystems.data.map((impactedSystem: ImpactedSystem) => {
        return {
          label: impactedSystem.codeDesc,
          value: impactedSystem.codeId,
        };
      });

      theOptions.sort((a, b) => {
        return String(a.label).localeCompare(String(b.label));
      });
    }

    return theOptions;
  }, [assets?.ListImpactedSystems?.data]);

  /**
   * Option values for event types dropdown select input.
   */
  const eventTypeOptions: SelectOption[] = useMemo(() => {
    let theOptions: SelectOption[] = [];

    if (assets?.ListEventTypes?.data) {
      theOptions = assets.ListEventTypes.data.map((eventType: EventType) => {
        return {
          label: eventType.codeDesc,
          value: eventType.codeId,
        };
      });

      theOptions.sort((a, b) => {
        return String(a.label).localeCompare(String(b.label));
      });
    }

    return theOptions;
  }, [assets?.ListEventTypes?.data]);

  /**
   * Watches form changes and triggers validation for new values.
   */
  useEffect(() => {
    if (isEmpty(currentEntity)) {
      const subscription = form.watch((values, { name }) => {
        if (name && !isEmpty(values[name])) {
          form.validate(
            name,
            () => {},
            () => {}
          );
        }
      });
      return () => {
        return subscription.unsubscribe();
      };
    }
  }, [form, currentEntity]);

  return (
    <ErrorHandler location="src/entities/Event/ui/components/Form/Form.tsx">
      <FormProvider autoComplete="off" onSubmit={handleSubmit} state={form}>
        <Grid>
          {isLoadingAssets ? (
            <Grid.Col span={{ xs: '100%' }}>
              <Loader height="100%" verticalAlignment="start" width="100%" />
            </Grid.Col>
          ) : (
            [
              <Grid.Col key="title" span={{ xs: '50%' }}>
                <TextInput
                  {...title}
                  onChange={() => {
                    form.validate(
                      'title',
                      () => {},
                      () => {}
                    );
                  }}
                  validators={{
                    ...title.validators,
                    validate: {
                      customValidator: (value: unknown) => {
                        return fieldValidator(title, value);
                      },
                    },
                  }}
                />
              </Grid.Col>,
              <Grid.Col key="eventTypeCode" span={{ xs: '50%' }}>
                <SelectInput
                  {...eventTypeCode}
                  onChange={() => {
                    form.validate(
                      'eventTypeCode',
                      () => {},
                      () => {}
                    );
                  }}
                  options={eventTypeOptions}
                  validators={{
                    ...eventTypeCode.validators,
                    validate: {
                      customValidator: (value: unknown) => {
                        return fieldValidator(eventTypeCode, value);
                      },
                    },
                  }}
                />
              </Grid.Col>,
              <Grid.Col key="startDate" span={{ xs: '50%' }}>
                <DateInput
                  {...startDate}
                  onChange={() => {
                    form.validate(
                      'startDate',
                      () => {},
                      () => {}
                    );
                  }}
                  validators={{
                    ...startDate.validators,
                    validate: {
                      customValidator: (value: unknown) => {
                        return fieldValidator(startDate, value);
                      },
                    },
                  }}
                />
              </Grid.Col>,
              <Grid.Col key="endDate" span={{ xs: '50%' }}>
                <DateInput
                  {...endDate}
                  onChange={() => {
                    form.validate(
                      'endDate',
                      () => {},
                      () => {}
                    );
                  }}
                  validators={{
                    ...endDate.validators,
                    validate: {
                      customValidator: (value: unknown) => {
                        return fieldValidator(endDate, value);
                      },
                    },
                  }}
                />
              </Grid.Col>,
              <Grid.Col key="expectedRecordCount" span={{ xs: '50%' }}>
                <TextInput
                  {...expectedRecordCount}
                  onChange={() => {
                    form.validate(
                      'expectedRecordCount',
                      () => {},
                      () => {}
                    );
                  }}
                  validators={{
                    ...expectedRecordCount.validators,
                    validate: {
                      customValidator: (value: unknown) => {
                        return fieldValidator(expectedRecordCount, value);
                      },
                    },
                  }}
                />
              </Grid.Col>,
              <Grid.Col key="actualRecordCount" span={{ xs: '50%' }}>
                <TextInput
                  {...actualRecordCount}
                  onChange={() => {
                    form.validate(
                      'actualRecordCount',
                      () => {},
                      () => {}
                    );
                  }}
                  validators={{
                    ...actualRecordCount.validators,
                    validate: {
                      customValidator: (value: unknown) => {
                        return fieldValidator(actualRecordCount, value);
                      },
                    },
                  }}
                />
              </Grid.Col>,
              <Grid.Col key="notes" span={{ xs: '50%' }}>
                <TextInputArea
                  {...notes}
                  onChange={() => {
                    form.validate(
                      'notes',
                      () => {},
                      () => {}
                    );
                  }}
                />
              </Grid.Col>,
              <Grid.Col key="impactedSystems" span={{ xs: '50%' }}>
                <SelectInputMulti
                  {...impactedSystems}
                  onChange={() => {
                    form.validate(
                      'impactedSystems',
                      () => {},
                      () => {}
                    );
                  }}
                  options={impactedSystemOptions}
                  validators={{
                    ...impactedSystems.validators,
                    validate: {
                      customValidator: (value: unknown) => {
                        return fieldValidator(impactedSystems, value);
                      },
                    },
                  }}
                />
              </Grid.Col>,
              <Grid.Col key="actions" span={{ xs: '100%' }}>
                <Divider height={1} />
                <Layout.Group alignLayout="right">
                  <Button onClick={handleClose} variant="outline">
                    Cancel
                  </Button>
                  <Button isDisabled={isDisabled} type="submit" variant="solid">
                    {!isEmpty(currentEntity) ? 'Save' : 'Create'}
                  </Button>
                </Layout.Group>
              </Grid.Col>,
            ]
          )}
        </Grid>
      </FormProvider>
    </ErrorHandler>
  );
}
